import { useEffect, useRef } from 'react';
import clsx from 'clsx';
import styles from './CropFrame.module.css';

/**
 * 6x6 grid with rule of thirds emphasized.
 * Reused from the original CropOverlay component.
 */
function CropGrid() {
  return (
    <div className={styles.grid}>
      {/* Horizontal lines */}
      <div className={clsx(styles.line, styles.h)} style={{ top: '16.666%' }} />
      <div className={clsx(styles.line, styles.h, styles.thirds)} style={{ top: '33.333%' }} />
      <div className={clsx(styles.line, styles.h, styles.center)} style={{ top: '50%' }} />
      <div className={clsx(styles.line, styles.h, styles.thirds)} style={{ top: '66.666%' }} />
      <div className={clsx(styles.line, styles.h)} style={{ top: '83.333%' }} />

      {/* Vertical lines */}
      <div className={clsx(styles.line, styles.v)} style={{ left: '16.666%' }} />
      <div className={clsx(styles.line, styles.v, styles.thirds)} style={{ left: '33.333%' }} />
      <div className={clsx(styles.line, styles.v, styles.center)} style={{ left: '50%' }} />
      <div className={clsx(styles.line, styles.v, styles.thirds)} style={{ left: '66.666%' }} />
      <div className={clsx(styles.line, styles.v)} style={{ left: '83.333%' }} />
    </div>
  );
}

const HANDLES = ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'];
const INNER_ROT_CORNERS = ['nw', 'ne', 'se', 'sw'];

/**
 * Fixed-frame crop editor. The frame stays at the target aspect ratio
 * and the user zooms/pans the image underneath. What's visible in the
 * frame IS the final crop.
 *
 * Resize handles map drag distance to zoom level.
 * Rotation zones outside corners/edges control crop tilt.
 * Inner rotation zones inside corners also trigger tilt drag.
 */
export default function CropFrame({
  vizUrl,
  frameW,
  frameH,
  baseScale,
  zoom,
  panX,
  panY,
  cropRotation,
  imgNaturalW,
  imgNaturalH,
  sourceW,
  sourceH,
  onMouseDown,
  onWheel,
  onZoomIn,
  onZoomOut,
  onHandleDrag,
  onRotationDrag,
}) {
  const frameRef = useRef(null);

  // Attach wheel listener with { passive: false } to ensure preventDefault works
  useEffect(() => {
    const el = frameRef.current;
    if (!el || !onWheel) return;
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [onWheel]);

  if (!vizUrl || !frameW || !frameH) return null;

  const s = baseScale * zoom;
  const imgDisplayW = imgNaturalW * s;
  const imgDisplayH = imgNaturalH * s;
  // Crop dimensions reported in SOURCE pixels (what /recrop will produce).
  // If source dims are unknown, we suppress the badge entirely — preview
  // pixels would mislead because they aren't what gets exported.
  const haveSourceDims = sourceW && sourceH && imgNaturalW && imgNaturalH;
  const previewCropW = s > 0 ? frameW / s : 0;
  const previewCropH = s > 0 ? frameH / s : 0;
  const cropW = haveSourceDims ? Math.round(previewCropW * (sourceW / imgNaturalW)) : 0;
  const cropH = haveSourceDims ? Math.round(previewCropH * (sourceH / imgNaturalH)) : 0;

  // Image position: centered in frame, offset by pan
  const imgStyle = {
    width: `${imgDisplayW}px`,
    height: `${imgDisplayH}px`,
    transform: `translate(-50%, -50%) translate(${panX}px, ${panY}px) rotate(${cropRotation}deg)`,
  };

  return (
    <div className={styles.cropContainer}>
      <div className={styles.darkSurround} />
      {/* Wrapper allows handles/rotation zones to overflow outside the frame */}
      <div
        className={styles.frameWrapper}
        style={{ width: `${frameW}px`, height: `${frameH}px` }}
      >
        <div
          ref={frameRef}
          className={styles.frame}
          onMouseDown={(e) => {
            e.preventDefault();
            onMouseDown?.(e);
          }}
        >
          <img
            className={styles.frameImage}
            src={vizUrl}
            alt="crop preview"
            style={imgStyle}
            draggable={false}
          />
          {cropW > 0 && cropH > 0 && (
            <div className={styles.dimsBadge}>{cropW}×{cropH}</div>
          )}
          <CropGrid />
          {(onZoomIn || onZoomOut) && (
            <div className={styles.zoomControls}>
              <button
                className={styles.zoomBtn}
                onClick={(e) => { e.stopPropagation(); onZoomIn?.(); }}
                onMouseDown={(e) => e.stopPropagation()}
                title="Zoom in (tighter crop)"
              >
                +
              </button>
              <button
                className={styles.zoomBtn}
                onClick={(e) => { e.stopPropagation(); onZoomOut?.(); }}
                onMouseDown={(e) => e.stopPropagation()}
                title="Zoom out (wider crop)"
              >
                -
              </button>
            </div>
          )}
          {/* Inner rotation zones - inside the frame corners for tilt drag */}
          {INNER_ROT_CORNERS.map((corner) => (
            <span
              key={`inner-rot-${corner}`}
              className={clsx(styles.innerRotZone, styles[`innerRot_${corner}`])}
              onMouseDown={(e) => {
                e.stopPropagation();
                e.preventDefault();
                onRotationDrag?.(corner, e);
              }}
            />
          ))}
        </div>
        {/* Resize handles - positioned relative to frameWrapper (overflow visible) */}
        {HANDLES.map((h) => (
          <span
            key={h}
            className={clsx(styles.handle, styles[`handle_${h}`])}
            onMouseDown={(e) => {
              e.stopPropagation();
              e.preventDefault();
              onHandleDrag?.(h, e);
            }}
          />
        ))}
        {/* Rotation zones - invisible areas outside the frame for rotation drag */}
        {HANDLES.map((zone) => (
          <span
            key={`rot-${zone}`}
            className={clsx(styles.rotationZone, styles[`rot_${zone}`])}
            onMouseDown={(e) => {
              e.stopPropagation();
              e.preventDefault();
              onRotationDrag?.(zone, e);
            }}
          />
        ))}
      </div>
    </div>
  );
}
